import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnInit,
    Renderer2,
    ViewChild
} from '@angular/core';
import {DynamicFormService} from '../../../dynamic-form/dynamic-form.service';
import {BaseFieldComponent} from '../../base/base-field/base-field.component';
import {EventDispatcherService} from '../../../events/event-dispatcher.service';
import {ChipsFieldConfig} from '../chips-field.config';
import {ChipItem} from '@mydocma/component-library/src/lib/chips/chip-item';

import {Validators} from '@angular/forms';
import {customEmailValidator, CustomValidatorFn, emailRegExp} from '../../../validation/custom-validatror';
import {CHIP_LIST_ERROR_KEY, ChipValidationError} from '../../../validation/custom-validation-error';
import {ChipListOptions} from '@mydocma/component-library/src/lib/chips/chip-list/chip-list-options';
import {ChipListStartAddNewEnum, ChipListViewTypeEnum} from '@mydocma/component-library/src/lib/chips';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

const INVALID_CHIP_CLASS = 'mydocma-chip-invalid';

@UntilDestroy()
@Component({
    selector: 'mydocma-form-chips-field',
    templateUrl: './chips-field.component.html',
    styleUrls: ['./chips-field.component.scss'],
})
export class ChipsFieldComponent extends BaseFieldComponent<ChipsFieldConfig> implements OnInit, AfterViewInit, AfterViewChecked {
    @ViewChild('container', {static: false}) container;
    @ViewChild('chip', {static: false}) chip;
    public readonly staticCssClass = 'mydocma-chips-field';
    chipListOption: ChipListOptions = {hideRemoveAll: true};

    constructor(
        public readonly dynamicFormService: DynamicFormService,
        public readonly changeDetectorRef: ChangeDetectorRef,
        public readonly eventDispatcher: EventDispatcherService,
        element: ElementRef,
        renderer: Renderer2
    ) {
        super(dynamicFormService, changeDetectorRef, eventDispatcher, element, renderer);
    }

    ngAfterViewChecked(): void {
      this.changeDetectorRef.detectChanges();
    }

    public ngOnInit() {
        super.ngOnInit();

        this.control.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(() => this.markInvalidChips());
        this.observeTouched();

        this.setChiplistReadonlyView();
    }

    public ngAfterViewInit(): void {
        this.onResized({contentRect: {height: this.container?.nativeElement?.offsetHeight}}, this.container?.nativeElement);

    }

    public onChipAdd(chip: ChipItem, chipList): void {
        chipList.classList.remove('add-mode');
        this.control.markAsTouched();
        this.control.setValue([...this.control.value, chip]);
        this.control.updateValueAndValidity();
        setTimeout(()=> this.onResized({contentRect: {height: this.container.nativeElement.offsetHeight}}, this.container.nativeElement));
    }

    public onChipRemove(chip: ChipItem): void {
        const index = this.control.value.indexOf(chip);

        if (index > -1) {
            const val = [...this.control.value];
            val.splice(index, 1);
            this.control.markAsTouched();
            this.control.setValue(val);
            this.control.updateValueAndValidity();
        }
        setTimeout(()=> this.onResized({contentRect: {height: this.container.nativeElement.offsetHeight}}, this.container.nativeElement));

    }

    public onAllChipRemove(): void {
        this.control.setValue([]);
        this.control.markAsTouched();
        this.control.updateValueAndValidity();
    }

    public onResized($event: { contentRect?: any; }, chipList: HTMLDivElement): void {
        if(this.fieldWrapper.config.options.viewType !== ChipListViewTypeEnum.multiLine){
            return;
        }
        if (this.control.invalid && this.control.touched) {
            if ($event.contentRect.height > 71.8125) {
                chipList.classList.add('multilined');
            } else {
                chipList.classList.remove('multilined');
            }
            return;
        }
        if ((this.control.invalid && !this.control.touched) || this.control.valid) {
            if ($event.contentRect.height > 51.8125) {
                chipList.classList.add('multilined');
            } else {
                chipList.classList.remove('multilined');
            }
        }

    }

    public onAddNewChipClick(chipList: HTMLDivElement): void {
        chipList.classList.add('add-mode');

    }

    public onExitAddMode(chipList: HTMLDivElement): void {
        chipList.classList.remove('add-mode');
    }

    public onFieldWrapperUpdated() {
        super.onFieldWrapperUpdated();
        this.setChiplistReadonlyView();
        this.markInvalidChips();
    }


    private observeTouched(): void {
        const control = this.control;
        const originalMarkAsTouched = control.markAsTouched;

        control.markAsTouched = (...args) => {
            originalMarkAsTouched.apply(control, args);
            this.markInvalidChips();
        };
    }


    private markInvalidChips(): void {
        const chips = (this.control.value ?? []) as ChipItem[];
        const invalidChips = this.control.touched ? this.getInvalidChips(chips) : [];

        chips.forEach((chip) => this.setChipInvalid(chip, invalidChips.includes(chip)));
    }


    private getInvalidChips(chips: ChipItem[]): ChipItem[] {
        const chipListError = this.control.errors?.[CHIP_LIST_ERROR_KEY] as ChipValidationError[];
        if (chipListError?.length) {
            return chipListError.map((chipError) => chipError.invalidChip);
        }

        if (this.hasEmailValidator()) {
            return chips.filter((chip) => !emailRegExp.test(chip?.text ?? ''));
        }

        return [];
    }

    private hasEmailValidator(): boolean {
        const validators = this.fieldWrapper.config.validators;
        const validatorList = Array.isArray(validators) ? validators : [validators];

        return validatorList.some((validator) => validator === Validators.email || validator === customEmailValidator);
    }

    private setChipInvalid(chip: ChipItem, invalid: boolean): void {
        const chipClassList = new Set((chip.class ?? '').split(' ').filter((cssClass) => !!cssClass));

        if (invalid) {
            chipClassList.add(INVALID_CHIP_CLASS);
        } else {
            chipClassList.delete(INVALID_CHIP_CLASS);
        }

        chip.class = [...chipClassList].join(' ');
    }

    private setChiplistReadonlyView() {
        const chipListOptions = {...this.fieldWrapper.config.options};

        if (this.fieldWrapper.config?.readonly || this.fieldWrapper.config?.disabled) {
            if(chipListOptions.addNewOptions?.startAdding){
                chipListOptions.addNewOptions.startAdding = ChipListStartAddNewEnum.none;
            }
            chipListOptions.hideRemoveAll = true;
            (this.control.value as ChipItem[]).forEach(value => value.readonly = true);
        }
        this.chipListOption = chipListOptions;
    }
}
